import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import { ReportStatusCellComponent } from '../financial-fees/report-status-cell/report-status-cell.component';
import {
  DataService,
  SecurityService,
  SystemService,
  Utilities,
  WorkflowContextService
} from 'src/app/services';
import { Actions, WorkflowType } from 'src/app/models';
import {
  FilterBuilderFilter,
  FilterBuilderOutput,
  FilterBuilderParam
} from 'src/app/models/filter-builder';
import {
  PagedPaymentReportResponse,
  PagedPaymentsReportRequest,
  PaymentsReportRequest,
  PaymentTransaction
} from 'src/app/models/financial-reports';
import { ApplicationStatusPipe } from 'src/app/pipes/application-status.pipe';
import { ToastrService } from 'ngx-toastr';
import { ComponentIds } from 'src/app/models/component-ids';
import { ColumnOption } from 'src/app/models/grid-settings';
import { Router, ActivatedRoute } from '@angular/router';
import { RefundButtonCellComponent } from './refund-button-cell/refund-button-cell.component';
import { NumbersCellComponent } from './numbers-cell/numbers-cell.component';
import { ClickableCellComponent } from 'src/app/components/common/common-table/clickable-cell/clickable-cell.component';

@Component({
  selector: 'app-financial-payments',
  templateUrl: './financial-payments.component.html',
  styleUrls: ['./financial-payments.component.css'],
  standalone: false
})
export class FinancialPaymentsComponent implements OnInit {
  loading = true;
  errorMessage = '';
  id = ComponentIds.PAYMENTS;
  title = 'Payments';
  report: PagedPaymentReportResponse;
  defaultFilters: FilterBuilderFilter[];
  filterTypes = [
    {
      name: 'contains'
    },
    {
      name: 'is'
    },
    {
      name: 'in'
    },
    {
      name: 'range'
    }
  ];

  @Output() itemRefunded: EventEmitter<PaymentTransaction> = new EventEmitter<
    PaymentTransaction
  >();

  columns: TableColumn[];
  availableColumns: TableColumn[];
  columnOptions: ColumnOption[] = [];
  exportColumns: string[];
  public page = {
    size: 10,
    totalElements: 0,
    totalPages: 0,
    pageNumber: 0
  };
  public sortField: string;
  public sortDescending = false;
  lastQuery = {};
  rows: any[];

  timeFrame: FilterBuilderParam = {
    name: 'Confirmed Date',
    id: 'timeframe',
    inputType: 'date',
    types: ['range']
  };

  params: FilterBuilderParam[] = [
    {
      name: 'Permit Number',
      types: ['is', 'contains'],
      id: 'permitNumber'
    },
    {
      name: 'Amount',
      id: 'amount',
      types: ['is'],
      inputType: 'number'
    },
    {
      name: 'Payment Method',
      id: 'paymentMethod',
      types: ['is', 'in'],
      options: ['Online', 'CreditCardPOS', 'Check', 'Cash']
    },
    {
      name: 'App Type',
      id: 'applicationName',
      options: []
    },
    {
      name: 'Applicant',
      id: 'payer'
    },
    this.timeFrame,
    {
      name: 'Collected By',
      id: 'collectedBy',
      types: ['is', 'contains']
    },
    {
      name: 'Recipient',
      id: 'recipients',
      types: ['is', 'contains']
    }
  ];

  filters: FilterBuilderOutput = {};
  canRefund = false;

  public useExcelFormat: boolean;
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-report-fees-payment-janus';

  constructor(
    private systemSvc: SystemService,
    private dataSvc: DataService,
    private securitySvc: SecurityService,
    private toast: ToastrService,
    private _context: WorkflowContextService,
    private appStatusPipe: ApplicationStatusPipe,
    public router: Router,
    public route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.page.size = pageSize;
      }
    }

    this.defaultFilters = [
      new FilterBuilderFilter({
        type: 'range',
        value: 'this_1_months',
        queryType: 'range',
        param: this.timeFrame,
        paramId: this.timeFrame.id
      })
    ];

    this.securitySvc
      .isLoginEntitled(Actions.REFUND_PAYMENT)
      .subscribe(result => {
        this.canRefund = result;
      });

    this.getApplications();
    // this.getReport();

    this.columns = [
      {
        key: 'permitId',
        label: 'Permit #',
        headerType: 'text',
        cellType: 'component',
        showExportOption: true,
        component: ClickableCellComponent,
        componentInputs: row => ({
          text: row.permitId,
          row: row
        }),
        componentOutputs: {
          clicked: rowData => this.onNameClick(rowData)
        }
      },
      {
        key: 'numbers',
        label: '#',
        headerType: 'text',
        cellType: 'component',
        component: NumbersCellComponent,
        componentInputs: row => ({
          orderNumber: row.orderNumber,
          transactionNumber: row.transactionNumber
        }),
        showExportOption: true
      },
      {
        key: 'orderNumber',
        label: 'Order Number',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      },
      {
        key: 'transactionNumber',
        label: 'Transaction Number',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      },
      {
        key: 'amount',
        label: 'Amount',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'paymentMethod',
        label: 'Payment Method',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'paymentDate',
        label: 'Paid',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'applicationName',
        label: 'App Type',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'username',
        label: 'Applicant',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'appStatus',
        label: 'Status',
        headerType: 'text',
        cellType: 'component',
        component: ReportStatusCellComponent,
        componentInputs: row => ({
          status: row.appStatus
        }),
        showExportOption: true
      },
      {
        key: 'appDescription',
        label: 'Application Description',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      },
      {
        key: 'paymentRequestId',
        label: 'Payment Request ID',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      },
      {
        key: 'clientName',
        label: 'Jurisdiction',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      },
      {
        key: 'collectedBy',
        label: 'Collected By',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'recipients',
        label: 'Recipient',
        headerType: 'text',
        cellType: 'text',
        showExportOption: true
      },
      {
        key: 'isRefunded',
        label: 'Is Refunded',
        headerType: 'text',
        cellType: 'component',
        component: RefundButtonCellComponent,
        componentInputs: row => ({
          isRefunded: row.isRefunded,
          paymentRequestId: row.paymentRequestId,
          canRefund: this.canRefund
        }),
        componentOutputs: {
          refund: (id: string) => this.refundPayment(id)
        },
        showExportOption: true
      }
    ];

    this.columnOptions = this.columns.map(c => ({
      key: c.key,
      name: c.key,
      label: c.label,
      checked: !c.hidden,
      includeInExport: c.showExportOption ?? true
    }));

    this.setAvailableColumns();
  }

  setAvailableColumns() {
    this.availableColumns = this.columns;
  }

  isEquivalent(a: any, b: any) {
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);

    if (aProps.length !== bProps.length) {
      return false;
    }

    for (let i = 0; i < aProps.length; i++) {
      const propName = aProps[i];
      if (a[propName] !== b[propName]) {
        return false;
      }
    }
    return true;
  }

  getApplications() {
    this.dataSvc
      .getPublishedWorkflows(this._context.client, [
        WorkflowType.ContractorRegistration,
        WorkflowType.ContractorRenewal,
        WorkflowType.Other,
        WorkflowType.Permit,
        WorkflowType.Renewable,
        WorkflowType.MobileInspection
      ])
      .subscribe(workflows => {
        const param = this.params.find(p => p.id === 'applicationName');
        param.options = workflows
          .map(w => ({
            name: w.version.name,
            value: w.id
          }))
          .sort((a: any, b: any) => a.name - b.name);
      });
  }

  private getQuery(): PaymentsReportRequest {
    const filters = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }

    const query = {
      clientId: this._context.client.id,
      filters,
      useExcelFormat: this.useExcelFormat,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      exportColumns: this.exportColumns
    };

    return query;
  }

  private getPagedQuery(): PagedPaymentsReportRequest {
    const filters = [];
    for (const id in this.filters) {
      if (this.filters[id]) {
        filters.push(this.filters[id]);
      }
    }

    const query = {
      clientId: this._context.client.id,
      filters,
      pageNumber: this.page.pageNumber,
      pageSize: this.page.size,
      sortField: this.sortField,
      sortDescending: this.sortDescending
    };

    return query;
  }

  onSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }): void {
    if (event.sortField === null) {
      // reset to default sorting
      this.sortField = 'permitId'; // choose your default
      this.sortDescending = false;
    } else {
      this.sortField = event.sortField;
      this.sortDescending = event.sortDescending ?? false;
    }
    this.page.pageNumber = 0;
    this.getReport();
  }

  onRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.page.size = pageSize;
    this.page.pageNumber = 0;
    this.getReport();
  }

  onPageChange(page: number) {
    this.page.pageNumber = page;
    this.getReport();
  }

  onRetry() {
    this.getReport();
  }

  getReport() {
    const query = this.getPagedQuery();
    if (!this.isEquivalent(query, this.lastQuery)) {
      this.lastQuery = { ...query };
      this.loading = true;
      this.systemSvc.getPagedPaymentsReport(query).subscribe({
        next: report => {
          this.page.totalElements = report.recordCount;
          this.page.totalPages = report.pageCount;
          this.rows = report.items.map(i => {
            i['permitId'] = i.permitId;
            i['orderNumber'] = i.orderNumber;
            i['transactionNumber'] = i.transactionNumber;
            i['amount'] = Utilities.formatCurrency(i.amount);
            i['paymentMethod'] = i.paymentMethod;
            i['paymentDate'] = i.paymentDate;
            i['applicationName'] = i.applicationName;
            i['username'] = i.username;
            i['appStatus'] = this.appStatusPipe.transform(i.appStatus);
            i['appDescription'] = i.applicationDescription;
            i['paymentRequestId'] = i.paymentRequestId;
            i['clientName'] = i.clientName;
            i['isRefunded'] = i.isRefunded;
            i['collectedBy'] = i.collectedBy;
            i['recipients'] = i.recipients;
            return i;
          });
          this.report = report;
          this.loading = false;
        },
        error: err => {
          console.error(err);
          this.loading = false;
          this.errorMessage = 'Failed to load report. Please try again.';
        }
      });
    }
  }

  onExportColumnsChange(cols: string[]) {
    this.exportColumns = cols;
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  export() {
    const query = this.getQuery();
    this.systemSvc.exportPaymentsReport(query).subscribe(
      data => {
        if (this.useExcelFormat) {
          const blob = new Blob([data], {
            type:
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `payments-report-export-${new Date().getTime()}.xlsx`;
          link.click();
        } else {
          const blob = new Blob([data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = `payments-report-export-${new Date().getTime()}.csv`;
          link.click();
        }
      },
      err => {
        this.toast.error('Problem while downloading the file.');
        console.error(err);
      }
    );
  }

  refundPayment(e) {
    this.systemSvc.refundPaymentRequest(e).subscribe(result => {
      if (result) {
        // update local payment request to refunded
        const pr = this.report.items.find(p => p.paymentRequestId === e);
        if (pr) {
          pr.isRefunded = true;
          this.itemRefunded.next(pr);
        }
      }
    });
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   this.getReport();
  // }

  onFiltersChanged(filters: FilterBuilderOutput) {
    this.filters = filters;
    this.page.pageNumber = 0; // reset to first page
    this.getReport();
  }

  onColumnsChanged(columnSet: { columns: any[]; exportColumns: any[] }) {
    // Update visible columns
    const visibleKeys = columnSet.columns.map(c => c.name);

    this.columns = this.availableColumns.map(col => ({
      ...col,
      hidden: !visibleKeys.includes(col.key)
    }));

    // Update export columns
    this.exportColumns = columnSet.exportColumns.map(c => c.name);
  }

  // added by Vipul on 16-07-2026
  onNameClick(row: any) {
    const segments = this.router.routerState.snapshot.url
      .split('/')
      .slice(0, -3);
    this.router.navigate([...segments, 'workflow-applications', 'data'], {
      queryParams: { id: row.applicationId }
    });
  }
  // end by Vipul on 16-07-2026
}
